import { isYValue, mutations } from './yjs';

/**
 * A map of Edit instances by user identifiers.
 */
export interface Edits {
    [key: string]: {
        updatedAt: number;
        count: number;
    };
}

/**
 * A summary of a user's edits: how many and when was the last one performed.
 */
export interface Edit {
    updatedAt: number;
    count: number;
}

/**
 * Get the head edit, defined by a custom sort function.
 */
function getHeadEdit(
    edits: Edits,
    sort: (a: Edit, b: Edit) => number
): { user: string; updatedAt: number } | null {
    if (!edits) {
        return null;
    }

    const result = Object.entries(edits).sort((a, b) => sort(a[1], b[1]))[0];

    if (!result) {
        return null;
    }

    return {
        user: result[0],
        updatedAt: result[1].updatedAt,
    };
}

/**
 * Get first edit (date and user). E.g. the oldest one.
 */
export function getFirstEdit(edits: Edits): { user: string; updatedAt: number } | null {
    return getHeadEdit(edits, (a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));
}

/**
 * Get last edit (date and user). E.g. the most recent one.
 */
export function getLastEdit(edits: Edits): { user: string; updatedAt: number } | null {
    return getHeadEdit(edits, (a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
}

/**
 * Add edits from users to a map of edits.
 * It mutates the input object and also returns it for convenience.
 */
export function addUserEdits<T extends mutations.MutableData<Edits>>(
    edits: T,
    userIds: string[],
    opts?: { count?: number; updatedAt?: number }
): T {
    const updatedAt = opts?.updatedAt || Date.now();
    const result = edits as mutations.MutableData<Edits>;

    userIds.forEach((userId) => {
        const prevCount = mutations.getIn(result, [userId, 'count'] as const);
        const prevUpdatedAt = mutations.getIn(result, [userId, 'updatedAt'] as const);

        if (prevCount === undefined) {
            mutations.set(result, userId, {
                count: opts?.count || 1,
                updatedAt,
            });
        } else {
            mutations.setIn(
                result,
                [userId, 'count'] as const,
                (prevCount || 0) + (opts?.count || 1)
            );
            mutations.setIn(
                result,
                [userId, 'updatedAt'] as const,
                prevUpdatedAt && prevUpdatedAt > updatedAt ? prevUpdatedAt : updatedAt
            );
        }
    });

    return result as T;
}

/**
 * Clone a map of edits.
 */
export function cloneUserEdits(edits: Edits): Edits {
    return Object.fromEntries(
        Object.entries(edits).map(([author, details]) => [
            author,
            {
                count: details.count,
                updatedAt: details.updatedAt,
            },
        ])
    );
}

/**
 * List users who made edits (ordered by count).
 */
export function listUsersInEdits(edits: Edits): string[] {
    return Object.entries(edits)
        .sort((a, b) => {
            if (a[1].count < b[1].count) {
                return -1;
            }
            if (a[1].count > b[1].count) {
                return 1;
            }
            return a[1].updatedAt < b[1].updatedAt ? -1 : 1;
        })
        .map((a) => a[0]);
}

/**
 * Create the initial set of edits.
 */
export function createEdits(input: string[] | string = [], date: Date = new Date()): Edits {
    const users = Array.isArray(input) ? input : [input];
    const edits: Edits = {};

    users.filter(Boolean).forEach((user) => {
        edits[user] = {
            count: 1,
            updatedAt: date.getTime(),
        };
    });

    return edits;
}

/**
 * Merge and accumulate all edits stats.
 */
export function mergeEdits<T extends mutations.MutableData<Edits>>(
    a: T,
    b: mutations.MutableData<Edits>
): T {
    const result = (isYValue(a) ? a : { ...a }) as mutations.MutableData<Edits>;

    Array.from(mutations.entries(b)).forEach(([userId, current]) => {
        const existing = mutations.get(result, userId);
        if (existing) {
            mutations.setIn(
                result,
                [userId, 'count'] as const,
                mutations.get(existing, 'count') + mutations.get(current, 'count')
            );
            mutations.setIn(
                result,
                [userId, 'updatedAt'] as const,
                mutations.get(existing, 'updatedAt') > mutations.get(current, 'updatedAt')
                    ? mutations.get(existing, 'updatedAt')
                    : mutations.get(current, 'updatedAt')
            );
        } else {
            //
            mutations.set(result, userId, (b as Edits)[userId]);
        }
    });

    return result as T;
}
